import { change, DecoratedFormProps, InjectedFormProps, reduxForm } from 'redux-form';
import { FormError } from 'src/components/redux-form/FormError';
import React, { useState } from 'react';
import {
    cardNumberFormat,
    cvvValidator,
    emailList,
    expirationDateFormat,
    maxLength,
    minLength,
    minPhoneLength,
    required,
    Validators,
} from 'src/components/redux-form/utils/validators';
import classNames from 'classnames';
import { FieldInput } from 'src/components/redux-form/fields/FieldInput';
import styles from '../assets/password-recovery-screen.module.scss';
import { faSave } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Dispatch } from 'redux';
import { toast } from 'react-toastify';
import { FieldDate } from '../../../../../components/redux-form/fields/FieldDate';
import { asyncPersistIntakeCreditCard } from '../../../../../store/actions/intakeSent-actions';
import { push } from 'connected-react-router';
import Router from '../../../../../navigation/router';
import { routes } from '../../../../../navigation';
import { FieldSelectList } from '../../../../../components/redux-form/fields/FieldSelectList';
import { FieldInputMask } from '../../../../../components/redux-form/fields/FieldInputMask';

export interface CreditCardFormData {
    student: string;
    formId: string;

    payerName: string;
    otherName: string;
    patientName: string;
    typeOfCard: string;
    cardNumber: string;
    expirationDate: string;
    securityNumber: string;
    billingAddressStreet: string;
    billingAddressCity: string;
    billingAddressState: string;
    billingAddressZip: string;
    billingAddressPhone: string;
    billingAddressEmail: string;
    signature: string;
    todayDate: string;
}

interface CreditCardFormProps {
    initialValues: any;
    setIsLoader: any;
}

type InjectedProps = InjectedFormProps<CreditCardFormData, CreditCardFormProps>;

const validators: Validators<CreditCardFormData> = {
    payerName: [maxLength({ max: 255 })],
    otherName: [maxLength({ max: 255 })],
    patientName: [maxLength({ max: 255 })],
    cardNumber: [maxLength({ max: 255 }), cardNumberFormat()],
    expirationDate: [maxLength({ max: 255 }), expirationDateFormat()],
    securityNumber: [maxLength({ max: 255 }), cvvValidator()],
    billingAddressStreet: [maxLength({ max: 255 })],
    billingAddressCity: [maxLength({ max: 255 })],
    billingAddressState: [maxLength({ max: 255 })],
    billingAddressZip: [maxLength({ max: 255 })],
    billingAddressPhone: [maxLength({ max: 255 })],
    billingAddressEmail: [maxLength({ max: 255 })],
    signature: [maxLength({ max: 255 })],
    todayDate: [maxLength({ max: 255 })],
};

const typeOfCardValues: SelectObject[] = [
    { label: 'VISA', value: 1 },
    { label: 'MASTERCARD', value: 2 },
    { label: 'AMEX', value: 3 },
    { label: 'DISCOVER', value: 4 },
];

const CreditCardForm: React.FunctionComponent<InjectedProps & CreditCardFormProps> = ({
    handleSubmit,
    error,
    dirty,
    submitting,
    initialValues,
    setIsLoader,
}: InjectedProps & CreditCardFormProps) => {
    return (
        <form onSubmit={handleSubmit} className={classNames(styles.form)}>
            <h2>ABA: Required Debit/Credit card Authorization</h2>
            <div style={{ textAlign: 'center' }}></div>
            <FormError error={error} />
            <FieldInput
                name="student"
                type="hidden"
                wrapperConfig={{
                    label: '',
                }}
            />
            <FieldInput
                name="formId"
                type="hidden"
                value={3}
                wrapperConfig={{
                    label: '',
                }}
            />
            <div style={{ paddingTop: '20px' }}>
                <FieldInput
                    name="payerName"
                    validate={validators.payerName}
                    wrapperConfig={{
                        classes: {
                            label: 'fullSize',
                        },
                        label: 'Payor Name (Print Last, First, Middle Initial) ',
                    }}
                />
            </div>
            <div>
                <FieldInput
                    name="otherName"
                    validate={validators.otherName}
                    wrapperConfig={{
                        classes: {
                            label: 'fullSize',
                        },
                        label: 'Name on card if different ',
                    }}
                />
            </div>
            <div>
                <div>
                    Related to the care of,{' '}
                    <span style={{ fontWeight: 700, fontStyle: 'italic' }}>[Patient's name]</span>, I authorize{' '}
                    <span style={{ textDecoration: 'underline' }}>Manhattan Psychology Group, PC</span> to charge my
                    card for all professional services that are not paid within 5 business days. I understand there is
                    an additional 3.5% administrative fee via PayPal which will automatically be added to my payment.
                </div>
                <FieldInput
                    name="patientName"
                    validate={validators.patientName}
                    wrapperConfig={{
                        classes: {
                            label: classNames(styles.boldLabel),
                        },
                        label: 'Please provide the Patient’s name here: ',
                    }}
                />
            </div>
            <div style={{ paddingTop: '40px' }}>
                <div>Type of card:</div>
                <FieldSelectList
                    multiple={false}
                    name="typeOfCard"
                    data={typeOfCardValues}
                    textField={'label'}
                    valueField={'value'}
                />
            </div>
            <div>
                <FieldInputMask
                    name="cardNumber"
                    mask="9999 9999 9999 9999"
                    validate={validators.cardNumber}
                    wrapperConfig={{
                        classes: {
                            label: 'fullSize',
                        },
                        label: 'Card Number (must be a non-HSA card)',
                    }}
                />
            </div>
            <div>
                <FieldInputMask
                    name="expirationDate"
                    mask="99/99"
                    validate={validators.expirationDate}
                    wrapperConfig={{
                        classes: {
                            label: 'fullSize',
                        },
                        label: 'Expiration Date (month/year)',
                    }}
                />
            </div>
            <div>
                <FieldInput
                    name="securityNumber"
                    validate={validators.securityNumber}
                    wrapperConfig={{
                        classes: {
                            label: 'fullSize',
                        },
                        label: 'Security Number ',
                    }}
                />
            </div>
            <div style={{ paddingTop: '40px' }}>
                <b>Card holder’s billing address:</b>
            </div>
            <div>
                <FieldInput
                    name="billingAddressStreet"
                    validate={validators.billingAddressStreet}
                    wrapperConfig={{
                        classes: {
                            label: 'fullSize',
                        },
                        label: 'Street/apt/floor ',
                    }}
                />
            </div>
            <div>
                <FieldInput
                    name="billingAddressCity"
                    validate={validators.billingAddressCity}
                    wrapperConfig={{
                        classes: {
                            label: 'fullSize',
                        },
                        label: 'City ',
                    }}
                />
            </div>
            <div>
                <FieldInput
                    name="billingAddressState"
                    validate={validators.billingAddressState}
                    wrapperConfig={{
                        classes: {
                            label: 'fullSize',
                        },
                        label: 'State ',
                    }}
                />
            </div>
            <div>
                <FieldInput
                    name="billingAddressZip"
                    validate={validators.billingAddressZip}
                    wrapperConfig={{
                        classes: {
                            label: 'fullSize',
                        },
                        label: 'Zip ',
                    }}
                />
            </div>
            <div>
                <FieldInput
                    name="billingAddressPhone"
                    validate={validators.billingAddressPhone}
                    wrapperConfig={{
                        classes: {
                            label: 'fullSize',
                        },
                        label: 'Telephone ',
                    }}
                />
            </div>
            <div>
                <FieldInput
                    name="billingAddressEmail"
                    validate={validators.billingAddressEmail}
                    wrapperConfig={{
                        classes: {
                            label: 'fullSize',
                        },
                        label: 'Email ',
                    }}
                />
            </div>
            <div style={{ paddingTop: '40px' }}>
                <b>Card holder’s signature</b>
            </div>
            <div>
                <FieldInput
                    name="signature"
                    validate={validators.signature}
                    wrapperConfig={{
                        classes: {
                            label: 'fullSize',
                        },
                        label: 'Signature',
                    }}
                />
            </div>
            <div>
                <FieldDate
                    name="todayDate"
                    type="text"
                    date={true}
                    validate={validators.todayDate}
                    wrapperConfig={{
                        label: 'Date ',
                    }}
                />
            </div>

            <div className={classNames(styles.actions)}>
                <button
                    type="submit"
                    className={classNames('btn', 'btn-success', 'mr-3')}
                    disabled={!dirty || submitting}
                >
                    <FontAwesomeIcon icon={faSave} /> Confirm and Send
                </button>
            </div>
        </form>
    );
};

export default reduxForm<CreditCardFormData, CreditCardFormProps>({
    form: 'credit-card-form',
    enableReinitialize: true,
    onSubmit: async (data: CreditCardFormData, dispatch: Dispatch<any>, props: CreditCardFormProps) => {
        props.setIsLoader(true);
        await asyncPersistIntakeCreditCard({ ...data }, dispatch);
    },
    onChange: async (
        values: Partial<CreditCardFormData>,
        dispatch: Dispatch<any>,
        props: any,
        previousValues: Partial<CreditCardFormData>,
    ) => {},
    onSubmitSuccess(
        result: any,
        dispatch: Dispatch<any>,
        props: DecoratedFormProps<CreditCardFormData, CreditCardFormProps, string>,
    ) {
        props.setIsLoader(false);
        dispatch(push(Router.generate(routes.INTAKE_PARENT_FORM_SUCCESS, {})));
    },

    onSubmitFail(errors: { _error: string }) {
        if (errors) {
            let field: any = null;
            Object.keys(errors).map((e: any, index: number, array: any) => {
                if (!field) {
                    field = e;
                    return;
                }
                return;
            });
            if (field) {
                const section = document.querySelector(`label[for='${field}']`);
                section?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        }
        if (errors._error) {
            toast.error(errors._error);
        }
    },
})(CreditCardForm);
